import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { logError } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { useLang, catLabel, CATEGORIES, STATUS_LABELS, timeAgo, ALERT_STATUS } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { StatusBadge } from "../components/StatusBadge";
import { unlockAudio, playAlarm } from "../lib/alarm";
import { Radio, LogOut, ChevronRight, MapPin, Settings, Plus, X, Loader2, TriangleAlert, Clock, Phone, MessageCircle, Bell, BellRing, BellOff, History, CheckCheck, Send, Mail } from "lucide-react";

const STATUSES = Object.keys(STATUS_LABELS.it);

export default function AdminDashboard() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("new");
  const [orders, setOrders] = useState(null);
  const [stats, setStats] = useState(null);
  const [techs, setTechs] = useState([]);
  const [filters, setFilters] = useState({ status: "", category: "", technician: "", date: "" });
  const [settings, setSettings] = useState({ zones: [], hours: "", emergency_note: "" });
  const [zoneInput, setZoneInput] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [techForm, setTechForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [techBusy, setTechBusy] = useState(false);
  const [techMsg, setTechMsg] = useState("");
  const [integ, setInteg] = useState(null);
  const [alertsCfg, setAlertsCfg] = useState(null);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [techEdits, setTechEdits] = useState({});
  const [techSaved, setTechSaved] = useState("");
  const [alarmArmed, setAlarmArmed] = useState(() => localStorage.getItem("psof_alarm") === "1");
  const [audioReady, setAudioReady] = useState(false);
  const seenRef = useRef(null);

  const toggleAlarm = () => {
    if (!audioReady) {
      const ok = unlockAudio();
      setAudioReady(ok);
      setAlarmArmed(true);
      localStorage.setItem("psof_alarm", "1");
      if (ok) { playAlarm(); toast.success(t("alarm_on")); }
    } else {
      const next = !alarmArmed;
      setAlarmArmed(next);
      localStorage.setItem("psof_alarm", next ? "1" : "0");
      if (next) { playAlarm(); toast.success(t("alarm_on")); } else toast(t("alarm_off"));
    }
  };

  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const { data } = await api.get("/admin/orders");
        const ids = data
          .filter((o) => (o.urgency === "emergenza" || o.urgency === "urgente") && !o.taken_charge_at && ["richiesta_ricevuta", "in_valutazione"].includes(o.status) && !o.is_demo)
          .map((o) => o.id);
        if (seenRef.current === null) { seenRef.current = new Set(ids); return; }
        const fresh = ids.filter((id) => !seenRef.current.has(id));
        seenRef.current = new Set(ids);
        if (fresh.length && alarmArmed && audioReady && !stop) {
          playAlarm();
          toast.warning(t("new_urgent_alert"), { duration: 8000 });
        }
      } catch (e) { logError(e); }
    };
    check();
    const iv = setInterval(check, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, [alarmArmed, audioReady, t]);

  const loadOrders = useCallback(async () => {
    const q = {};
    if (tab === "all") Object.entries(filters).forEach(([k, v]) => { if (v) q[k] = v; });
    const { data } = await api.get("/admin/orders", { params: q });
    setOrders(tab === "new" ? data.filter((o) => ["richiesta_ricevuta", "in_valutazione", "variazione_da_approvare"].includes(o.status)) : data);
  }, [tab, filters]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/technicians").then((r) => setTechs(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings({ zones: r.data.zones || [], hours: r.data.hours || "", emergency_note: r.data.emergency_note || "" })).catch(() => {});
    api.get("/admin/integration-status").then((r) => setInteg(r.data)).catch(() => {});
    api.get("/admin/alerts-config").then((r) => setAlertsCfg(r.data)).catch(() => {});
  }, []);

  const takeCharge = async (e, oid) => {
    e.stopPropagation();
    try { await api.post(`/orders/${oid}/take-charge`); loadOrders(); api.get("/admin/stats").then((r) => setStats(r.data)); } catch (err) { logError(err); toast.error("Operazione non riuscita"); }
  };

  const saveAlerts = async () => {
    setSavingAlerts(true);
    try { await api.put("/admin/alerts-config", { ...alertsCfg, retry_after_minutes: Number(alertsCfg.retry_after_minutes) || 5 }); } finally { setSavingAlerts(false); }
  };

  const saveTech = async (tc) => {
    const e = techEdits[tc.user_id] || {};
    const body = {
      skills: (e.skills ?? (tc.skills || []).join(", ")).split(",").map((s) => s.trim()).filter(Boolean),
      certifications: tc.certifications || [],
      zones: (e.zones ?? (tc.zones || []).join(", ")).split(",").map((s) => s.trim()).filter(Boolean),
      available: e.available ?? (tc.available ?? true),
      verified_documents: tc.verified_documents || [],
    };
    await api.put(`/admin/technicians/${tc.user_id}`, body);
    const r = await api.get("/admin/technicians"); setTechs(r.data);
    setTechSaved(tc.user_id); setTimeout(() => setTechSaved(""), 1500);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put("/settings", settings); } finally { setSavingSettings(false); }
  };

  const createTech = async () => {
    setTechBusy(true); setTechMsg("");
    try {
      await api.post("/admin/technicians", techForm);
      setTechForm({ name: "", email: "", phone: "", password: "" });
      const r = await api.get("/admin/technicians"); setTechs(r.data);
      setTechMsg("ok");
    } catch (e) { setTechMsg(e.response?.data?.detail ? String(e.response.data.detail) : "Errore"); }
    finally { setTechBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-3 text-white sm:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600"><Radio className="h-4 w-4" /></div>
          <div><p className="font-display text-sm font-extrabold leading-none">ProntoSof24 · {t("admin_area")}</p><p className="text-xs text-slate-400">{user?.email}</p></div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="alarm-toggle-btn" title={alarmArmed && audioReady ? t("alarm_on") : t("alarm_arm")} onClick={toggleAlarm}
            className={`rounded-full p-2 transition ${alarmArmed && audioReady ? "bg-rose-600 text-white animate-pulse" : "bg-slate-800 text-slate-300"}`}>
            {alarmArmed && audioReady ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
          </button>
          <button data-testid="admin-leads-btn" onClick={() => nav("/centrale/lead")} className="rounded-full bg-[#0E7C7B] px-3 py-2 text-xs font-bold text-white">Lead B2B</button>
          <button data-testid="alerts-history-btn" title={t("alerts_history")} onClick={() => nav("/centrale/avvisi")} className="rounded-full bg-slate-800 p-2 text-slate-300"><History className="h-4 w-4" /></button>
          <LangToggle />
          <button data-testid="logout-btn" onClick={() => logout().then(() => nav("/login"))} className="rounded-full bg-slate-800 p-2"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-8">
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <StatCard label={t("new_requests")} value={stats.new} color="text-blue-600" testid="stat-new" />
            <StatCard label={t("active_orders")} value={stats.active} color="text-amber-600" testid="stat-active" />
            <StatCard label={t("paid_orders")} value={stats.paid} color="text-emerald-600" testid="stat-paid" />
            <StatCard label={t("variation_pending")} value={stats.variations} color="text-orange-600" testid="stat-var" />
            <StatCard label={t("total_orders")} value={stats.total} color="text-slate-700" testid="stat-total" />
          </div>
        )}

        <div className="flex gap-2 border-b border-slate-200">
          {[["new", t("new_requests")], ["all", t("all_orders")], ["settings", t("settings")]].map(([id, label]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-bold transition ${tab === id ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"}`}>{label}</button>
          ))}
        </div>

        {tab === "all" && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">{t("filter_status")}</option>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[lang][s]}</option>)}
            </select>
            <select data-testid="filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">{t("filter_category")}</option>
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c[lang]}</option>)}
            </select>
            <select data-testid="filter-tech" value={filters.technician} onChange={(e) => setFilters({ ...filters, technician: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">{t("filter_tech")}</option>
              {techs.map((tc) => <option key={tc.user_id} value={tc.user_id}>{tc.name}</option>)}
            </select>
            <input data-testid="filter-date" type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
          </div>
        )}

        {tab === "settings" ? (
          <div className="max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-800">{t("zones_served")}</label>
              <div className="flex flex-wrap gap-2">
                {settings.zones.map((z, i) => (
                  <span key={i} className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">
                    {z}<button data-testid={`remove-zone-${i}`} onClick={() => setSettings({ ...settings, zones: settings.zones.filter((_, j) => j !== i) })}><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input data-testid="zone-input" value={zoneInput} onChange={(e) => setZoneInput(e.target.value)} placeholder="Roma" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <button data-testid="add-zone" onClick={() => { if (zoneInput.trim()) { setSettings({ ...settings, zones: [...settings.zones, zoneInput.trim()] }); setZoneInput(""); } }} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-800">{t("hours")}</label>
              <input data-testid="hours-input" value={settings.hours} onChange={(e) => setSettings({ ...settings, hours: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-800">{t("emergency_note") || "Nota"}</label>
              <input data-testid="note-input" value={settings.emergency_note} onChange={(e) => setSettings({ ...settings, emergency_note: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <button data-testid="save-settings" disabled={savingSettings} onClick={saveSettings} className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-bold text-white disabled:opacity-60">{savingSettings && <Loader2 className="h-4 w-4 animate-spin" />}{t("save")}</button>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <p className="mb-3 text-sm font-bold text-slate-800">{t("add_technician")}</p>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="tech-name" value={techForm.name} onChange={(e) => setTechForm({ ...techForm, name: e.target.value })} placeholder={t("name")} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <input data-testid="tech-phone" value={techForm.phone} onChange={(e) => setTechForm({ ...techForm, phone: e.target.value })} placeholder={t("phone")} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <input data-testid="tech-email" type="email" value={techForm.email} onChange={(e) => setTechForm({ ...techForm, email: e.target.value })} placeholder={t("email")} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <input data-testid="tech-password" type="text" value={techForm.password} onChange={(e) => setTechForm({ ...techForm, password: e.target.value })} placeholder={t("password")} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              {techMsg && <p className={`mt-2 text-xs font-semibold ${techMsg === "ok" ? "text-emerald-600" : "text-rose-600"}`}>{techMsg === "ok" ? "✓" : techMsg}</p>}
              <button data-testid="create-tech-btn" disabled={techBusy || !techForm.email || !techForm.password || !techForm.name} onClick={createTech} className="mt-3 flex items-center gap-2 rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{techBusy && <Loader2 className="h-4 w-4 animate-spin" />}{t("create")}</button>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="flex items-center gap-2 text-sm font-bold text-slate-800"><Bell className="h-4 w-4 text-blue-600" />{t("alerts_title")}</p>
                {integ && <span data-testid="integration-status-badge" className={`rounded-full border px-2.5 py-1 text-xs font-bold ${integ.channels?.telegram?.configured ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{integ.channels?.telegram?.configured ? t("integration_ready") : t("integration_to_configure")}</span>}
              </div>
              {integ?.channels && (
                <div className="mb-3 space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="integration-help">
                  <p className="font-semibold">{t("integration_help")}</p>
                  {Object.entries(integ.channels).map(([k, c]) => (
                    <div key={k} className="flex items-start justify-between gap-2">
                      <span className="font-semibold">{c.label}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 font-bold ${c.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{c.configured ? t("integration_ready") : ((c.missing || []).join(", ") || t("integration_to_configure"))}</span>
                    </div>
                  ))}
                  <p className="mt-1">{integ.note}</p>
                </div>
              )}
              {alertsCfg && (
                <div className="grid grid-cols-2 gap-2">
                  <LabeledInput label={t("whatsapp_number")} testid="cfg-wa" value={alertsCfg.whatsapp_to} onChange={(v) => setAlertsCfg({ ...alertsCfg, whatsapp_to: v })} />
                  <LabeledInput label={t("call_number")} testid="cfg-call" value={alertsCfg.call_to} onChange={(v) => setAlertsCfg({ ...alertsCfg, call_to: v })} />
                  <LabeledInput label={`${t("call_hours")} (start)`} testid="cfg-hstart" value={alertsCfg.call_hours_start} onChange={(v) => setAlertsCfg({ ...alertsCfg, call_hours_start: v })} />
                  <LabeledInput label={`${t("call_hours")} (end)`} testid="cfg-hend" value={alertsCfg.call_hours_end} onChange={(v) => setAlertsCfg({ ...alertsCfg, call_hours_end: v })} />
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500">{t("call_mode")}</label>
                    <select data-testid="cfg-mode" value={alertsCfg.call_mode} onChange={(e) => setAlertsCfg({ ...alertsCfg, call_mode: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                      <option value="all">{t("call_mode_all")}</option>
                      <option value="urgent_only">{t("call_mode_urgent")}</option>
                    </select>
                  </div>
                  <LabeledInput label={t("retry_after")} testid="cfg-retry" type="number" value={alertsCfg.retry_after_minutes} onChange={(v) => setAlertsCfg({ ...alertsCfg, retry_after_minutes: v })} />
                  <LabeledInput label={t("second_number")} testid="cfg-second" value={alertsCfg.second_number} onChange={(v) => setAlertsCfg({ ...alertsCfg, second_number: v })} className="col-span-2" />
                </div>
              )}
              <button data-testid="save-alerts" disabled={savingAlerts || !alertsCfg} onClick={saveAlerts} className="mt-3 flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">{savingAlerts && <Loader2 className="h-4 w-4 animate-spin" />}{t("save")}</button>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <p className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800"><CheckCheck className="h-4 w-4 text-blue-600" />{t("manage_techs")}</p>
              <div className="space-y-3">
                {techs.map((tc) => {
                  const e = techEdits[tc.user_id] || {};
                  return (
                    <div key={tc.user_id} data-testid={`tech-row-${tc.user_id}`} className="rounded-xl border border-slate-200 p-3">
                      <p className="text-sm font-bold text-slate-900">{tc.name}</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <LabeledInput label={t("skills")} testid={`tech-skills-${tc.user_id}`} value={e.skills ?? (tc.skills || []).join(", ")} onChange={(v) => setTechEdits({ ...techEdits, [tc.user_id]: { ...e, skills: v } })} />
                        <LabeledInput label={t("zones")} testid={`tech-zones-${tc.user_id}`} value={e.zones ?? (tc.zones || []).join(", ")} onChange={(v) => setTechEdits({ ...techEdits, [tc.user_id]: { ...e, zones: v } })} />
                      </div>
                      <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" data-testid={`tech-available-${tc.user_id}`} checked={e.available ?? (tc.available ?? true)} onChange={(ev) => setTechEdits({ ...techEdits, [tc.user_id]: { ...e, available: ev.target.checked } })} />
                        {t("available")}
                      </label>
                      <button data-testid={`tech-save-${tc.user_id}`} onClick={() => saveTech(tc)} className="mt-2 rounded-lg bg-slate-800 px-4 py-1.5 text-xs font-bold text-white">{techSaved === tc.user_id ? "✓" : t("save")}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {orders === null && <p className="text-sm text-slate-400">{t("loading")}</p>}
            {orders?.length === 0 && <p className="text-sm text-slate-400">{t("no_orders")}</p>}
            {orders?.map((o) => {
              const urgent = o.urgency === "emergenza" || o.urgency === "urgente";
              const flag = o.status === "variazione_da_approvare";
              return (
                <div key={o.id} role="button" tabIndex={0} data-testid={`admin-order-${o.id}`} onClick={() => nav(`/centrale/ordine/${o.id}`)}
                  className={`card-hover cursor-pointer rounded-2xl border bg-white p-4 text-left shadow-sm ${o.urgency === "emergenza" ? "border-rose-300 ring-2 ring-rose-100" : flag ? "border-orange-300 ring-2 ring-orange-100" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-blue-600">{o.code}</span>
                    <div className="flex items-center gap-1">
                      {o.is_demo && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">DEMO</span>}
                      {o.urgency === "emergenza" && <span className="flex items-center gap-0.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-extrabold text-rose-700"><TriangleAlert className="h-3 w-3" />{t("urgent")}</span>}
                    </div>
                  </div>
                  <p className="mt-1 text-sm font-bold text-slate-900">{o.problem}</p>
                  <p className="text-xs text-slate-500">{o.name} · {catLabel(o.category, lang)}</p>
                  <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{o.address?.city}</p>
                  <p data-testid={`elapsed-${o.id}`} className="mt-1 flex items-center gap-1 text-xs font-semibold text-slate-500"><Clock className="h-3 w-3" />{t("arrived_ago")} {timeAgo(o.created_at, lang)}</p>
                  {o.alerts && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <AlertChip icon={Send} label={t("alert_telegram")} st={o.alerts.telegram?.status} t={t} />
                      <AlertChip icon={MessageCircle} label={t("alert_whatsapp")} st={o.alerts.whatsapp?.status} t={t} />
                      <AlertChip icon={Phone} label={t("alert_call")} st={o.alerts.call?.status} t={t} />
                      {o.alerts.client_quote && <AlertChip icon={Mail} label={t("alert_email")} st={o.alerts.client_quote?.status} t={t} />}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between"><StatusBadge status={o.status} /><ChevronRight className="h-4 w-4 text-slate-300" /></div>
                  {!o.taken_charge_at && (
                    <button data-testid={`take-charge-${o.id}`} onClick={(e) => takeCharge(e, o.id)} className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-blue-600 py-2 text-xs font-bold text-white transition hover:bg-blue-700">
                      <CheckCheck className="h-3.5 w-3.5" />{t("take_charge")}
                    </button>
                  )}
                  {o.taken_charge_at && <p className="mt-2 flex items-center gap-1 text-xs font-bold text-emerald-600"><CheckCheck className="h-3.5 w-3.5" />{t("taken_charge")}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, color, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
    <p className={`font-display text-3xl font-extrabold ${color}`}>{value}</p>
    <p className="text-xs font-semibold text-slate-500">{label}</p>
  </div>
);

const LabeledInput = ({ label, value, onChange, type = "text", testid, className = "" }) => (
  <div className={className}>
    <label className="mb-1 block text-xs font-semibold text-slate-500">{label}</label>
    <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
  </div>
);

const AlertChip = ({ icon: Icon, label, st, t }) => {
  const meta = ALERT_STATUS[st] || ALERT_STATUS.pending;
  return (
    <span data-testid={`alert-chip-${label}-${st || "none"}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>
      <Icon className="h-3 w-3" />{label}: {t(meta.key)}
    </span>
  );
};
